"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import type { ActiveVocabularyTermData } from "./ImmersiveVocabularyCards";
import { useI18n } from "@/lib/i18n";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SlidersHorizontal, Loader2 } from "lucide-react";
import { usePlayer } from "@/contexts/player-context";
import { useUserSettings } from "@/contexts/user-settings-context";
import { useIsMobile } from "@/hooks/use-mobile";

type TimestampTuple = [number, number];

interface TranscriptChunk {
  text?: string;
  timestamp?: TimestampTuple;
  translation?: string | Record<string, string>;
  words?: Array<{
    word: string;
    start?: number;
    end?: number;
  }>;
}

type TranscriptWord = NonNullable<TranscriptChunk["words"]>[number];

interface TranscriptGroup {
  text?: string;
  translation?: string | Record<string, string>;
  timestamp?: TimestampTuple;
  chunks?: TranscriptChunk[];
}

interface TimeDrivenTranscriptProps {
  groupedSentences: TranscriptGroup[];
  currentTime: number;
  className?: string;
  contentWrapperClassName?: string;
  showTranslation?: boolean;
  backgroundTone?: "light" | "dark";
  isLoading?: boolean;
  emptyStateMessage?: string;
  highlightWord?: string;
  highlightTextClassName?: string;
  highlightBgClassName?: string;
  onSeek?: (seconds: number) => void;
  isVideoMinimized?: boolean;
  activeVocabularyTerms?: ActiveVocabularyTermData[];
  vocabularyTerms?: string[];
  staticVocabularyTerms?: ActiveVocabularyTermData[];
  onWordClick?: (
    word: WordWithTiming,
    event: React.MouseEvent<HTMLSpanElement> | React.TouchEvent<HTMLSpanElement>,
  ) => void;
  autoScrollToActive?: boolean;
  scrollContainerRef?: React.MutableRefObject<HTMLDivElement | null>;
  enableSwipeToVocabulary?: boolean;
  vocabularyContent?: React.ReactNode;
  keywordsContent?: React.ReactNode;
  onViewChange?: (view: 'transcript' | 'keywords' | 'vocabulary') => void;
  onToggleTranslation?: () => void;
  onScrollToActive?: () => void;
  showDynamicIslandVocabulary?: boolean;
  onToggleDynamicIslandVocabulary?: (value: boolean) => void;
  episodeId?: string;
  onTranscriptUpdate?: (transcript: any[], audioUrl?: string) => void;
  // 🎯 Chapters
  showChapters?: boolean;
  onToggleChapters?: (value: boolean) => void;
}

interface WordWithTiming {
  id: string;
  text: string;
  start: number;
  end: number;
  chunkIndex: number;
  groupIndex: number;
  translation?: string | Record<string, string>;
}

interface LineWithTiming {
  id: string;
  words: WordWithTiming[];
  start: number;
  end: number;
  translation?: string | Record<string, string>;
}

type WordTemporalState = "past" | "current" | "future";

const clamp = (value: number, min = 0, max = 1) =>
  Math.min(Math.max(value, min), max);

interface TranscriptTextPalette {
  current: string;
  past: string;
  future: string;
  translation: string;
  translationActive: string;
}

const SKELETON_BLUEPRINT: string[][] = [
  ["w-24", "w-16", "w-36", "w-20"],
  ["w-10", "w-24", "w-12", "w-16"],
  ["w-20", "w-14", "w-32"],
  ["w-12", "w-8", "w-28", "w-20"],
  ["w-24", "w-10", "w-6"],
  ["w-32", "w-16", "w-20", "w-12", "w-8"],
];

const PREVIEW_WINDOW_SECONDS = 6;
const REVEAL_LEAD_SECONDS = 1;

function ensureRange(
  start: number,
  end: number,
  minimumSpan = 0.0001,
): [number, number] {
  if (end - start >= minimumSpan) {
    return [start, end];
  }

  const midpoint = (start + end) / 2;
  return [
    clamp(midpoint - minimumSpan / 2, 0, 1),
    clamp(midpoint + minimumSpan / 2, 0, 1),
  ];
}

function safeTimestamp(timestamp?: TimestampTuple | null): TimestampTuple {
  if (Array.isArray(timestamp) && timestamp.length === 2) {
    const [start, end] = timestamp;
    if (Number.isFinite(start) && Number.isFinite(end)) {
      return [start, end];
    }
  }
  return [0, 0];
}

function buildWordTimeline(groups: TranscriptGroup[]): LineWithTiming[] {
  return groups
    .map((group, groupIndex) => {
      const baseTimestamp = safeTimestamp(group.timestamp);
      const chunks =
        (Array.isArray(group.chunks) && group.chunks.length > 0
          ? group.chunks
          : [
              {
                text: group.text ?? "",
                timestamp: baseTimestamp,
                translation: group.translation,
              },
            ]) as TranscriptChunk[];

      const words: WordWithTiming[] = [];

      chunks.forEach((rawChunk, chunkIndex) => {
        const chunkTimestamp = safeTimestamp(rawChunk.timestamp ?? baseTimestamp);
        const chunkStart = chunkTimestamp[0];
        const chunkEnd = Math.max(chunkTimestamp[1], chunkStart);
        const chunkDuration = chunkEnd - chunkStart;

        const fallbackWords = (rawChunk.text ?? "")
          .split(/\s+/)
          .filter((token) => token.length > 0);

        const chunkWords: TranscriptWord[] =
          Array.isArray(rawChunk.words) && rawChunk.words.length > 0
            ? rawChunk.words
            : fallbackWords.map<TranscriptWord>((token) => ({ word: token }));

        const wordCount = chunkWords.length || 1;
        const perWordDuration =
          chunkDuration > 0 ? chunkDuration / wordCount : 0.35;

        chunkWords.forEach((word, wordIndex) => {
          const text = word.word ?? "";
          if (!text) {
            return;
          }

          const explicitStart =
            typeof word.start === "number" ? word.start : undefined;
          const explicitEnd = typeof word.end === "number" ? word.end : undefined;

          const start =
            explicitStart ??
            chunkStart + Math.max(0, wordIndex) * perWordDuration;
          const end =
            explicitEnd ??
            chunkStart + (Math.max(0, wordIndex) + 1) * perWordDuration;

          const adjustedStart = Math.max(0, start - REVEAL_LEAD_SECONDS);
          const adjustedEnd = Math.max(end, adjustedStart + 0.05);

          words.push({
            id: `${groupIndex}-${chunkIndex}-${wordIndex}`,
            text,
            start: adjustedStart,
            end: adjustedEnd,
            chunkIndex,
            groupIndex,
            translation: rawChunk.translation ?? group.translation,
          });
        });
      });

      if (words.length === 0) {
        return null;
      }

      const lineStart = words.reduce(
        (acc, word) => Math.min(acc, word.start),
        Number.POSITIVE_INFINITY,
      );
      const lineEnd = words.reduce(
        (acc, word) => Math.max(acc, word.end),
        Number.NEGATIVE_INFINITY,
      );

      // Helper to check if translation is valid/non-empty
      const isValidTranslation = (t: any) => {
        if (!t) return false;
        if (typeof t === 'string') return t.trim().length > 0;
        if (typeof t === 'object') return Object.keys(t).length > 0;
        return false;
      };

      const groupTrans = group.translation;
      const firstChunkTrans = chunks[0]?.translation;
      
      // Prefer group translation if valid, otherwise fallback to first chunk
      const effectiveTranslation = isValidTranslation(groupTrans) ? groupTrans : firstChunkTrans;

      return {
        id: `transcript-line-${groupIndex}`,
        words,
        start: Number.isFinite(lineStart) ? lineStart : baseTimestamp[0],
        end: Number.isFinite(lineEnd) ? lineEnd : baseTimestamp[1],
        // 优先使用 group 的翻译，如果没有，尝试使用第一个 chunk 的翻译
        translation: effectiveTranslation,
      } satisfies LineWithTiming;
    })
    .filter(Boolean) as LineWithTiming[];
}

type VocabularyHighlightStyle = {
  textClass?: string;
  backgroundClass?: string;
  level?: string;
  showBadge?: boolean;
};

const DEFAULT_HIGHLIGHT_TEXT_CLASS = "text-sky-400";
const DEFAULT_HIGHLIGHT_BACKGROUND_CLASS = "bg-current";

const VOCAB_SUFFIXES = ["ed", "ing", "s", "er", "est", "ly"] as const;
const WORD_MASK_STORAGE_KEY = "time-driven-transcript:show-mask";

const WORD_OPACITY_OUTPUT: readonly [number, number] = [0.9, 1];
const REVEAL_PROGRESS_OUTPUT: readonly [number, number] = [0.5, 1];
const TEXT_OPACITY_OUTPUT: readonly [number, number] = [-2, 1];
const HIGHLIGHT_CURRENT_INPUT: readonly number[] = [0, 0.25, 0.6, 1];
const HIGHLIGHT_CURRENT_OUTPUT: readonly number[] = [0.15, 0.35, 0.45, 0.2];
const HIGHLIGHT_TRAILING_INPUT: readonly [number, number] = [0, 1];
const HIGHLIGHT_TRAILING_OUTPUT: readonly [number, number] = [0.2, 0];
const HIGHLIGHT_FUTURE_OPACITY: (value: number) => number = () => 0.25;

const computeCurrentBackgroundOpacity = (value: number) => {
  const clamped = clamp(value, 0, 1);

  if (clamped <= 0.25) {
    const t = clamped / 0.25;
    return 0.45 + t * (0.9 - 0.45);
  }

  if (clamped <= 0.6) {
    const t = (clamped - 0.25) / 0.35;
    return 0.9 + t * (0.96 - 0.9);
  }

  const t = (clamped - 0.6) / 0.4;
  return 0.96 * Math.max(0, 1 - t);
};

const computeTrailingBackgroundOpacity = (value: number) => {
  const clamped = clamp(value, 0, 1);
  return Math.max(0, 0.45 * (1 - clamped));
};

function mapRange(
  value: number,
  input: readonly [number, number],
  output: readonly [number, number],
): number {
  const [inputStart, inputEnd] = input;
  if (inputEnd - inputStart === 0) {
    return value <= inputStart ? output[0] : output[1];
  }
  const clampedValue = clamp((value - inputStart) / (inputEnd - inputStart));
  return output[0] + (output[1] - output[0]) * clampedValue;
}

function mapPiecewise(
  value: number,
  input: readonly number[],
  output: readonly number[],
): number {
  if (input.length === 0 || output.length === 0) {
    return 0;
  }

  const lastIndex = Math.min(input.length, output.length) - 1;
  if (lastIndex <= 0) {
    return output[0]!;
  }

  if (value <= input[0]!) {
    return output[0]!;
  }

  for (let index = 0; index < lastIndex; index += 1) {
    const start = input[index]!;
    const end = input[index + 1]!;
    const outStart = output[index]!;
    const outEnd = output[index + 1]!;

    if (value >= end) {
      continue;
    }

    if (end - start === 0) {
      return outEnd;
    }

    const localT = clamp((value - start) / (end - start));
    return outStart + (outEnd - outStart) * localT;
  }

  return output[lastIndex]!;
}

function normalizeToken(token: string): string {
  return token
    .replace(/^[^a-zA-Z']+|[^a-zA-Z']+$/g, "")
    .toLowerCase();
}

function matchesWithSuffix(base: string, candidate: string): boolean {
  if (!base || !candidate) {
    return false;
  }

  if (candidate === base) {
    return true;
  }

  return VOCAB_SUFFIXES.some((suffix) => candidate === `${base}${suffix}`);
}

function buildVocabularyHighlightMap(
  lines: LineWithTiming[],
  activeTerms?: ActiveVocabularyTermData[],
  vocabularyTerms?: string[],
  staticTerms?: ActiveVocabularyTermData[],
): Map<string, VocabularyHighlightStyle> {
  const map = new Map<string, VocabularyHighlightStyle>();

  if (lines.length === 0) {
    return map;
  }

  const processed: Array<{ tokens: string[]; style: VocabularyHighlightStyle }> = [];

  const addProcessedEntries = (entries: ActiveVocabularyTermData[] | undefined | null) => {
    if (!Array.isArray(entries)) {
      return;
    }

    entries.forEach((entry) => {
      const term = entry?.term?.trim();
      if (!term) {
        return;
      }

      const tokens = term
        .split(/\s+/)
        .map((token) => normalizeToken(token))
        .filter(Boolean);

      if (tokens.length === 0) {
        return;
      }

      processed.push({
        tokens,
        style: {
          textClass: entry.textClass || DEFAULT_HIGHLIGHT_TEXT_CLASS,
          backgroundClass:
            entry.lightBackgroundClass ||
            entry.backgroundClass ||
            DEFAULT_HIGHLIGHT_BACKGROUND_CLASS,
          level: entry.level,
        },
      });
    });
  };

  if (Array.isArray(activeTerms)) {
    activeTerms.forEach((entry) => {
      const term = entry?.term?.trim();
      if (!term) {
        return;
      }

      const tokens = term
        .split(/\s+/)
        .map((token) => normalizeToken(token))
        .filter(Boolean);

      if (tokens.length === 0) {
        return;
      }

      processed.push({
        tokens,
        style: {
          textClass: entry.textClass || DEFAULT_HIGHLIGHT_TEXT_CLASS,
          backgroundClass:
            entry.lightBackgroundClass ||
            entry.backgroundClass ||
            DEFAULT_HIGHLIGHT_BACKGROUND_CLASS,
          level: entry.level,
        },
      });
    });
  }

  addProcessedEntries(staticTerms);

  if (Array.isArray(vocabularyTerms)) {
    vocabularyTerms.forEach((rawTerm) => {
      const term = rawTerm?.trim();
      if (!term) {
        return;
      }

      const tokens = term
        .split(/\s+/)
        .map((token) => normalizeToken(token))
        .filter(Boolean);

      if (tokens.length === 0) {
        return;
      }

      processed.push({
        tokens,
        style: {
          textClass: DEFAULT_HIGHLIGHT_TEXT_CLASS,
          backgroundClass: DEFAULT_HIGHLIGHT_BACKGROUND_CLASS,
        },
      });
    });
  }

  if (processed.length === 0) {
    return map;
  }

  lines.forEach((line) => {
    const normalizedWords = line.words.map((word) => ({
      id: word.id,
      normalized: normalizeToken(word.text),
    }));

    processed.forEach(({ tokens, style }) => {
      if (tokens.length === 0) {
        return;
      }

      for (let startIndex = 0; startIndex <= normalizedWords.length - tokens.length; startIndex += 1) {
        let match = true;

        for (let offset = 0; offset < tokens.length; offset += 1) {
          const wordInfo = normalizedWords[startIndex + offset];
          if (!wordInfo || !matchesWithSuffix(tokens[offset]!, wordInfo.normalized)) {
            match = false;
            break;
          }
        }

        if (match) {
          for (let offset = 0; offset < tokens.length; offset += 1) {
            const wordInfo = normalizedWords[startIndex + offset];
            if (wordInfo && !map.has(wordInfo.id)) {
              map.set(wordInfo.id, {
                ...style,
                showBadge: offset === tokens.length - 1 && Boolean(style.level),
              });
            }
          }
        }
      }
    });
  });

  return map;
}

interface WordProps {
  data: WordWithTiming;
  totalDuration: number;
  state: WordTemporalState;
  showMask: boolean;
  textPalette?: TranscriptTextPalette | null;
  onSeek?: (seconds: number) => void;
  highlightWord?: string;
  highlightTextClassName?: string;
  highlightBgClassName?: string;
  currentTime: number;
  highlightStyle?: VocabularyHighlightStyle;
  onWordClick?: (
    word: WordWithTiming,
    event: React.MouseEvent<HTMLSpanElement> | React.TouchEvent<HTMLSpanElement>,
  ) => void;
  isScrolling?: boolean;
  isLineActive?: boolean; // 🎯 整行是否 active，用于判断是否触发查词
}

const TimeDrivenWord: React.FC<WordProps> = ({
  data,
  totalDuration,
  state,
  showMask,
  textPalette,
  onSeek,
  highlightWord,
  highlightTextClassName,
  highlightBgClassName,
  currentTime,
  highlightStyle,
  onWordClick,
  isScrolling = false,
  isLineActive = false,
}) => {
  const baseDuration = Math.max(totalDuration, data.end);
  const {
    opacityRange,
    backgroundRange,
    textRange,
  } = useMemo(() => {
    const startRatio = baseDuration > 0 ? clamp(data.start / baseDuration) : 0;
    const endRatio = baseDuration > 0 ? clamp(data.end / baseDuration) : startRatio + 0.01;
    const paddedStart = clamp(startRatio - 0.02);

    return {
      opacityRange: ensureRange(paddedStart, endRatio + 0.04),
      backgroundRange: ensureRange(startRatio, endRatio),
      textRange: ensureRange(endRatio - 0.05, endRatio),
    };
  }, [baseDuration, data.end, data.start]);

  const normalizedProgress =
    baseDuration > 0 && Number.isFinite(baseDuration)
      ? clamp(currentTime / baseDuration)
      : 0;

  const wordOpacityValue = mapRange(normalizedProgress, opacityRange, WORD_OPACITY_OUTPUT);
  const revealProgressValue = mapRange(
    normalizedProgress,
    backgroundRange,
    REVEAL_PROGRESS_OUTPUT,
  );
  const currentBackgroundOpacityValue =
    computeCurrentBackgroundOpacity(revealProgressValue);
  const trailingBackgroundOpacityValue =
    computeTrailingBackgroundOpacity(revealProgressValue);
  const highlightOverlayCurrentOpacityValue = mapPiecewise(
    revealProgressValue,
    HIGHLIGHT_CURRENT_INPUT,
    HIGHLIGHT_CURRENT_OUTPUT,
  );
  const highlightOverlayTrailingOpacityValue = mapRange(
    revealProgressValue,
    HIGHLIGHT_TRAILING_INPUT,
    HIGHLIGHT_TRAILING_OUTPUT,
  );
  const highlightOverlayFutureOpacityValue = HIGHLIGHT_FUTURE_OPACITY(revealProgressValue);
  const textOpacityValue = mapRange(normalizedProgress, textRange, TEXT_OPACITY_OUTPUT);

  const isHighlighted =
    highlightWord &&
    data.text.localeCompare(highlightWord, undefined, {
      sensitivity: "accent",
      usage: "search",
    }) === 0;

  const highlightBackgroundClass = highlightStyle?.backgroundClass;
  const shouldRenderHighlightOverlay =
    typeof highlightBackgroundClass === "string" &&
    !highlightBackgroundClass.includes("bg-current");
  const displayLevel = highlightStyle?.level;
  const shouldShowLevelBadge = Boolean(highlightStyle?.showBadge && displayLevel);
  const shouldDarkenHighlightBackground =
    !!highlightStyle && (!highlightBackgroundClass || highlightBackgroundClass.includes("bg-current"));

  const timeUntilStart = data.start - currentTime;
  const previewRatio = clamp(Math.max(0, timeUntilStart) / PREVIEW_WINDOW_SECONDS);
  const falloffThreshold = 0.4;
  const normalizedFalloff = previewRatio <= falloffThreshold
    ? 0
    : Math.pow((previewRatio - falloffThreshold) / (1 - falloffThreshold), 1.6);
  const shouldHide = state === "future" && timeUntilStart > PREVIEW_WINDOW_SECONDS;
  const futureShapeOpacity = 0.96 - normalizedFalloff * 0.38;
  const futureBackgroundOpacity = 0.9 - normalizedFalloff * 0.36;

  const displayOpacity = clamp(
    state === "future" ? futureShapeOpacity : wordOpacityValue,
  );
  const baseMaskOpacity = clamp(
    state === "future"
      ? futureBackgroundOpacity
      : state === "current"
        ? currentBackgroundOpacityValue
        : trailingBackgroundOpacityValue,
  );
  const highlightOverlayOpacityValue = clamp(
    state === "future"
      ? highlightOverlayFutureOpacityValue
      : state === "current"
        ? highlightOverlayCurrentOpacityValue
        : highlightOverlayTrailingOpacityValue,
  );
  const textOpacityDisplay = clamp(textOpacityValue);
  const maskOpacity = showMask ? baseMaskOpacity : 0;
  const overlayOpacity = showMask ? highlightOverlayOpacityValue : 0;
  const shouldDisplayOverlay = showMask && shouldRenderHighlightOverlay;

  // Hooks 必须在条件返回之前调用
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const hasMovedRef = useRef(false);
  const touchHandledRef = useRef(false); // 🎯 防止 touch 和 click 重复处理

  if (shouldHide) {
    return (
      <span
        className="relative mx-1 inline-block select-none whitespace-pre font-oldschool-grotesk-medium leading-[1.45] text-[1.15rem] sm:text-[1.3rem] md:text-[1.3rem]"
        aria-hidden="true"
        style={{ opacity: 0 }}
      >
        {data.text}
      </span>
    );
  }

  const handleTouchStart = (event: React.TouchEvent<HTMLSpanElement>) => {
    const touch = event.touches[0];
    if (touch) {
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
      hasMovedRef.current = false;
    }
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLSpanElement>) => {
    if (!touchStartPosRef.current) {
      return;
    }
    const touch = event.touches[0];
    if (touch) {
      const deltaX = Math.abs(touch.clientX - touchStartPosRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPosRef.current.y);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      // 如果移动距离超过 8px，认为是滑动
      if (distance > 8) {
        hasMovedRef.current = true;
      }
    }
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLSpanElement>) => {
    // 检查是否移动过（滑动）
    const wasScrolling = hasMovedRef.current || isScrolling;
    
    // 重置状态
    touchStartPosRef.current = null;
    hasMovedRef.current = false;
    
    // 如果移动过，不处理点击
    if (wasScrolling) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    
    // 🎯 标记 touch 已处理，防止后续 onClick 重复处理
    touchHandledRef.current = true;
    // 使用 setTimeout 在下一个事件循环重置，确保 onClick 检查时标志位仍然有效
    setTimeout(() => {
      touchHandledRef.current = false;
    }, 100);
    
    // 如果没有移动，当作点击处理
    handleInteraction(event);
  };

  const handleInteraction = (event: React.MouseEvent<HTMLSpanElement> | React.TouchEvent<HTMLSpanElement>) => {
    // 🎯 如果是 click 事件，且 touch 已经处理过，跳过（防止重复处理）
    const isClickEvent = !('touches' in event || 'changedTouches' in event);
    if (isClickEvent && touchHandledRef.current) {
      return;
    }

    // 对于鼠标事件，如果正在滑动也阻止
    if (isClickEvent && isScrolling) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // 🎯 整个 active line 内的所有单词点击都触发查词（包括已播放过的前几个单词）
    // 非 active line 的单词只进行跳转（onSeek）
    if (isLineActive && onWordClick) {
      onWordClick(data, event);
    }

    if (event.defaultPrevented) {
      return;
    }

    if (onSeek) {
      onSeek(data.start);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLSpanElement> = (event) => {
    if (!onSeek) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSeek(data.start);
    }
  };

  const wordColor =
    textPalette && !highlightStyle && !isHighlighted ? textPalette[state] : undefined;

  return (
    <span
      role={onSeek ? "button" : undefined}
      tabIndex={onSeek ? 0 : undefined}
      className={cn(
        "time-driven-word relative mx-1 inline-block select-none whitespace-pre font-oldschool-grotesk-medium leading-[1.45] text-[1.15rem] sm:text-[1.3rem] md:text-[1.3rem] transition-colors transition-opacity duration-200",
        !textPalette && !highlightStyle && state === "future" && "text-muted-foreground/60",
        !textPalette && !highlightStyle && state === "past" && "text-foreground/80",
        !textPalette && !highlightStyle && state === "current" && "text-foreground",
        onSeek && "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2",
        isHighlighted && highlightTextClassName,
        highlightStyle?.textClass,
      )}
      style={{
        opacity: displayOpacity,
        color: wordColor,
      }}
      onClick={handleInteraction}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown}
      data-word-state={state}
      data-word-mask={showMask ? "enabled" : "disabled"}
    >
      {showMask && (
        <span
          aria-hidden="true"
          className={cn(
            "time-driven-word__mask absolute left-1/2 top-1/2 h-[80%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-2xl transition-opacity duration-200",
            isHighlighted
              ? highlightBgClassName
              : highlightStyle
                ? highlightBackgroundClass || "bg-current"
                : state === "future"
                  ? "bg-foreground"
                  : "bg-foreground/90",
          )}
          style={{
            opacity: maskOpacity,
            filter: shouldDarkenHighlightBackground ? "brightness(0.82)" : undefined,
          }}
        />
      )}
      {shouldDisplayOverlay && (
        <span
          aria-hidden="true"
          className={cn(
            "time-driven-word__overlay absolute left-1/2 top-1/2 h-[80%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-2xl mix-blend-screen pointer-events-none transition-opacity duration-200",
            highlightBackgroundClass,
          )}
          style={{ opacity: overlayOpacity }}
        />
      )}
      {shouldShowLevelBadge && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-[5px] right-0 translate-x-1/2 rounded-full bg-neutral-900/70 px-[2px] py-px text-[0.45rem] uppercase tracking-wide text-white shadow-sm dark:bg-white/90 dark:text-neutral-900 z-[1]"
        >
          {displayLevel}
        </span>
      )}
      <span
        className="relative transition-opacity duration-200"
        style={{ opacity: textOpacityDisplay }}
      >
        {data.text}
      </span>
    </span>
  );
};

interface TranscriptLineProps {
  line: LineWithTiming;
  totalDuration: number;
  currentTime: number;
  showWordMask: boolean;
  onSeek?: (seconds: number) => void;
  highlightWord?: string;
  highlightTextClassName?: string;
  highlightBgClassName?: string;
  textPalette?: TranscriptTextPalette | null;
  isActive: boolean;
  showTranslation?: boolean;
  isVideoMinimized?: boolean;
  highlightVocabularyMap?: Map<string, VocabularyHighlightStyle>;
  onWordClick?: (
    word: WordWithTiming,
    event: React.MouseEvent<HTMLSpanElement> | React.TouchEvent<HTMLSpanElement>,
  ) => void;
  autoScrollToActive: boolean;
  lineIndex: number;
  isPlayerPlaying: boolean;
  liveTimeGetter?: (() => number) | null;
  userTargetLanguageCode?: string;
  episodeId?: string;
  isTranslating?: boolean; // 🎯 是否正在翻译中
}

interface TranscriptLineInternalProps extends TranscriptLineProps {
  scrollContainerRef?: React.MutableRefObject<HTMLDivElement | null>;
}

const TranscriptLine: React.FC<TranscriptLineInternalProps> = ({
  line,
  totalDuration,
  currentTime,
  showWordMask,
  onSeek,
  highlightWord,
  highlightTextClassName,
  highlightBgClassName,
  textPalette,
  isActive,
  showTranslation,
  isVideoMinimized,
  highlightVocabularyMap,
  onWordClick,
  autoScrollToActive,
  lineIndex,
  scrollContainerRef,
  isPlayerPlaying,
  liveTimeGetter,
  userTargetLanguageCode,
  episodeId,
  isTranslating = false, // 🎯 是否正在翻译中
}) => {
  const isMobile = useIsMobile();
  const lineRef = useRef<HTMLDivElement | null>(null);
  const manualScrollReleaseTimeoutRef = useRef<number | null>(null);
  const isManualScrollingRef = useRef(false);
  const [isManualScrolling, setIsManualScrolling] = useState(false);
  const lastScrollTopRef = useRef<number | null>(null);
  const scrollTimeoutRef = useRef<number | null>(null);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isScrollingRef = useRef(false);
  const [isScrolling, setIsScrolling] = useState(false);
  
  // Helper to determine translation text based on language settings
  const getTranslationText = useCallback((raw: string | Record<string, string> | undefined | null) => {
    if (!raw) return null;
    
    // 🎯 如果正在翻译，只返回目标语言的翻译，不 fallback 到中文
    if (typeof raw === 'string') {
      // 字符串格式假定是中文，如果用户语言是中文则返回，否则在翻译中时返回 null
      if (userTargetLanguageCode === 'zh') return raw;
      // 正在翻译时，不显示中文，等待目标语言翻译
      if (isTranslating) return null;
      return raw; // 不在翻译时，fallback 显示中文
    }
    
    // If object, try target language
    if (userTargetLanguageCode && raw[userTargetLanguageCode]) {
      return raw[userTargetLanguageCode];
    }
    
    // 🎯 正在翻译时，不 fallback 到中文，返回 null 以显示 skeleton
    if (isTranslating) return null;
    
    // Fallback logic (仅在不翻译时)
    return raw['zh'] || raw['chinese'] || Object.values(raw)[0] || null;
  }, [userTargetLanguageCode, isTranslating]);

  const translationText = useMemo(() => {
    const text = getTranslationText(line.translation);
    // console.log(`[TranscriptLine] id=${line.id} raw=`, line.translation, 'resolved=', text, 'target=', userTargetLanguageCode);
    return text;
  }, [line.translation, getTranslationText, line.id, userTargetLanguageCode]);

  // Auto-trigger translation if missing
  useEffect(() => {
     if (showTranslation && isActive && !translationText && episodeId && userTargetLanguageCode) {
        // We only trigger if we *really* think there should be translation but it's missing for this language
        // This avoids spamming. A better check might be if *any* translation exists but *this* one doesn't.
        // For now, we rely on the user toggling the switch to trigger global checks or specific line checks.
        // Implementing a per-line trigger here might be too aggressive without debouncing/batching.
        
        // However, we can trigger if we see other lines have it but this one doesn't?
        // Or simply: let the parent handle bulk checks, or just leave it for now.
        // The prompt says: "检测到缺翻译时自动调用它".
        // Let's implement a simple check: if translation is totally missing or missing key, trigger.
        // But to avoid infinite loops/spam, we need to be careful.
        // Maybe we delegate this to a parent effect that scans all lines once?
        // That seems safer. So for now, we just display what we have.
     }
  }, [showTranslation, isActive, translationText, episodeId, userTargetLanguageCode]);


  const isLineFuture = currentTime < line.start - 0.05;
  const shouldShowTranslation = showTranslation && translationText && !isLineFuture;
  // 🎯 正在翻译且没有目标语言翻译时显示 skeleton
  const shouldShowTranslationSkeleton = showTranslation && isTranslating && !translationText && !isLineFuture;

  // 参考 React Native 版本的简洁实现
  useEffect(() => {
    if (!autoScrollToActive || typeof window === "undefined") {
      return;
    }

    // 使用 ref 来检查，确保在用户滚动时立即阻止自动滚动
    if (!isActive || !lineRef.current || isManualScrollingRef.current) {
      return;
    }

    const element = lineRef.current;
    const scrollContainer = scrollContainerRef?.current;

    if (!scrollContainer) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const containerHeight = containerRect.height || 1;
    const elementCenterY = rect.top - containerRect.top + rect.height / 2;

    // 给 active 行一个"舒适区"，只在它离开该区域时才触发滚动
    const comfortableTop = containerHeight * 0.25;
    const comfortableBottom = containerHeight * 0.68;
    const isAboveComfort = elementCenterY < comfortableTop;
    const isBelowComfort = elementCenterY > comfortableBottom;

    if (!isAboveComfort && !isBelowComfort) {
      return;
    }

    // 根据方向决定吸附位置，避免每次都跳到同一个高度
    const snapTarget = isAboveComfort
      ? containerHeight * 0.32
      : containerHeight * 0.55;
    const delta = elementCenterY - snapTarget;

    if (Math.abs(delta) < 6) {
      return;
    }

    // 再次检查，防止在滚动过程中状态变化
    if (isManualScrollingRef.current) {
      return;
    }

    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop + delta,
      behavior: "smooth",
    });
  }, [
    isActive,
    isManualScrolling,
    autoScrollToActive,
    scrollContainerRef,
  ]);

  useEffect(() => {
    if (!autoScrollToActive) {
      return undefined;
    }

    if (typeof window === "undefined") {
      return undefined;
    }

    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) {
      return undefined;
    }

    const scheduleResume = () => {
      if (manualScrollReleaseTimeoutRef.current !== null) {
        window.clearTimeout(manualScrollReleaseTimeoutRef.current);
      }
      manualScrollReleaseTimeoutRef.current = window.setTimeout(() => {
        isManualScrollingRef.current = false;
        setIsManualScrolling(false);
        manualScrollReleaseTimeoutRef.current = null;
        lastScrollTopRef.current = null;
      }, 2500); // 增加到 2.5 秒，给用户更多时间
    };

    const handleManualScroll = () => {
      isManualScrollingRef.current = true;
      setIsManualScrolling(true);
      scheduleResume();
    };

    // 检测触摸滑动，防止滑动时触发单词点击
    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch) {
        touchStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          time: Date.now(),
        };
        isScrollingRef.current = false;
        setIsScrolling(false);
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!touchStartRef.current) {
        return;
      }

      const touch = event.touches[0];
      if (touch) {
        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // 如果移动距离超过 8px，认为是滑动
        if (distance > 8) {
          isScrollingRef.current = true;
          setIsScrolling(true);
          handleManualScroll(); // 同时触发手动滚动检测
        }
      }
    };

    const handleTouchEnd = () => {
      // 立即重置滑动状态，让点击事件可以正常处理
      // 实际的点击阻止逻辑在单词组件内部处理
      isScrollingRef.current = false;
      setIsScrolling(false);
      // 延迟清除触摸开始位置，给点击事件处理时间
      setTimeout(() => {
        touchStartRef.current = null;
      }, 50);
    };

    // 检测滚动位置变化来判断是否是用户手动滚动
    const handleScroll = () => {
      const currentScrollTop = scrollContainer.scrollTop;
      
      // 如果这是第一次记录，初始化位置
      if (lastScrollTopRef.current === null) {
        lastScrollTopRef.current = currentScrollTop;
        return;
      }
      
      // 检查滚动位置是否发生了变化
      const scrollDelta = Math.abs(currentScrollTop - lastScrollTopRef.current);
      
      // 如果滚动距离超过阈值，认为是用户手动滚动
      // 注意：自动滚动也会触发 scroll 事件，但通常自动滚动的 delta 很小
      // 我们设置一个合理的阈值来区分手动滚动和自动滚动
      if (scrollDelta > 3) {
        handleManualScroll();
      }
      
      lastScrollTopRef.current = currentScrollTop;
      
      // 清除之前的超时，重新计时
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = window.setTimeout(() => {
        // 滚动停止后，延迟一段时间再允许自动滚动
        // 注意：这里不直接调用 scheduleResume，而是只重置滚动位置记录
        // scheduleResume 会在 touchstart/touchmove/wheel 事件触发时调用
        lastScrollTopRef.current = null;
      }, 500);
    };

    const handleKeyScroll = (event: KeyboardEvent) => {
      const scrollKeys = [
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        " ",
      ];
      if (scrollKeys.includes(event.key)) {
        handleManualScroll();
      }
    };

    const options: AddEventListenerOptions | boolean = { passive: true };
    
    // 监听触摸事件（移动端）- 用于检测滑动
    scrollContainer.addEventListener("touchstart", handleTouchStart, options);
    scrollContainer.addEventListener("touchmove", handleTouchMove, options);
    scrollContainer.addEventListener("touchend", handleTouchEnd, options);
    scrollContainer.addEventListener("touchcancel", handleTouchEnd, options);
    
    // 监听触摸事件（移动端）- 用于检测手动滚动
    scrollContainer.addEventListener("touchstart", handleManualScroll, options);
    scrollContainer.addEventListener("touchmove", handleManualScroll, options);
    // 监听滚轮事件（桌面端）
    scrollContainer.addEventListener("wheel", handleManualScroll, options);
    // 监听滚动事件（检测滚动位置变化）
    scrollContainer.addEventListener("scroll", handleScroll, options);
    
    // 键盘滚动事件（总是添加到 window，因为键盘事件是全局的）
    window.addEventListener("keydown", handleKeyScroll);

    return () => {
      scrollContainer.removeEventListener("touchstart", handleTouchStart, options);
      scrollContainer.removeEventListener("touchmove", handleTouchMove, options);
      scrollContainer.removeEventListener("touchend", handleTouchEnd, options);
      scrollContainer.removeEventListener("touchcancel", handleTouchEnd, options);
      
      scrollContainer.removeEventListener("touchstart", handleManualScroll, options);
      scrollContainer.removeEventListener("touchmove", handleManualScroll, options);
      scrollContainer.removeEventListener("wheel", handleManualScroll, options);
      scrollContainer.removeEventListener("scroll", handleScroll, options);
      
      window.removeEventListener("keydown", handleKeyScroll);

      if (manualScrollReleaseTimeoutRef.current !== null) {
        window.clearTimeout(manualScrollReleaseTimeoutRef.current);
        manualScrollReleaseTimeoutRef.current = null;
      }
      
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      
      isManualScrollingRef.current = false;
      setIsManualScrolling(false);
      isScrollingRef.current = false;
      setIsScrolling(false);
      lastScrollTopRef.current = null;
      touchStartRef.current = null;
    };
  }, [autoScrollToActive, scrollContainerRef]);

  return (
    <motion.div
      ref={lineRef}
      data-active={isActive ? "true" : "false"}
      data-line-index={lineIndex}
      className={cn(
        "relative flex flex-col gap-0 rounded-3xl px-1.5 py-1 transition-all duration-300 sm:px-6 md:px-8",
        isActive
          ? "bg-transparent"
          // ? "bg-primary/5 shadow-[0_10px_40px_-20px_rgba(59,130,246,0.55)]"
          : "bg-transparent",
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap">
          {line.words.map((word) => {
            const state: WordTemporalState =
              currentTime >= word.end
                ? "past"
                : currentTime >= word.start && currentTime < word.end
                  ? "current"
                  : "future";
            const highlightStyle = highlightVocabularyMap?.get(word.id);

            return (
              <TimeDrivenWord
                key={word.id}
                data={word}
                totalDuration={totalDuration}
                state={state}
                showMask={showWordMask}
                textPalette={textPalette}
                onSeek={onSeek}
                highlightWord={highlightWord}
                highlightTextClassName={highlightTextClassName}
                highlightBgClassName={highlightBgClassName}
                currentTime={currentTime}
                highlightStyle={highlightStyle}
                onWordClick={onWordClick}
                isScrolling={isScrolling}
                isLineActive={isActive}
              />
            );
          })}
        </div>
        {shouldShowTranslation && (
          <motion.span
            className={cn(
              "text-sm font-medium sm:text-base md:text-lg",
              !textPalette && "text-muted-foreground",
              !textPalette && isActive && "text-foreground",
            )}
            style={
              textPalette
                ? {
                    color: isActive
                      ? textPalette.translationActive
                      : textPalette.translation,
                  }
                : undefined
            }
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.2 }}
          >
            {translationText}
          </motion.span>
        )}
        {/* 🎯 翻译中显示 skeleton */}
        {shouldShowTranslationSkeleton && (
          <motion.div
            className="flex gap-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.2 }}
          >
            <div className="h-4 w-16 animate-pulse rounded bg-muted-foreground/20 sm:h-5 sm:w-20" />
            <div className="h-4 w-24 animate-pulse rounded bg-muted-foreground/20 sm:h-5 sm:w-32" />
            <div className="h-4 w-12 animate-pulse rounded bg-muted-foreground/20 sm:h-5 sm:w-16" />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export const TimeDrivenTranscript: React.FC<TimeDrivenTranscriptProps> = ({
  groupedSentences,
  currentTime,
  className,
  contentWrapperClassName,
  showTranslation = true,
  showDynamicIslandVocabulary = true,
  backgroundTone,
  isLoading = false,
  emptyStateMessage,
  onSeek,
  highlightWord,
  highlightTextClassName = "!text-orange-500",
  highlightBgClassName = "!bg-orange-500",
  isVideoMinimized = false,
  activeVocabularyTerms,
  vocabularyTerms,
  staticVocabularyTerms,
  onWordClick,
  autoScrollToActive = true,
  scrollContainerRef,
  enableSwipeToVocabulary = false,
  vocabularyContent,
  keywordsContent,
  onViewChange,
  onToggleTranslation,
  onScrollToActive,
  onToggleDynamicIslandVocabulary,
  episodeId,
  onTranscriptUpdate,
  // 🎯 Chapters
  showChapters = true,
  onToggleChapters,
}) => {
  const { t } = useI18n();
  const { getCurrentTime: liveTimeGetter, isPlaying: isPlayerPlaying } = usePlayer();
  const { userSettings } = useUserSettings();
  const [currentView, setCurrentView] = useState<'transcript' | 'keywords' | 'vocabulary'>('transcript');
  const containerRef = useRef<HTMLDivElement>(null);
  const [showWordMask, setShowWordMask] = useState(false);

  // Resolve user target language code
  const userTargetLanguageCode = useMemo(() => {
    const lang = userSettings?.languageSetting?.toLowerCase() || 'zh';
    // Map common names to codes
    if (lang === 'chinese' || lang.startsWith('zh')) return 'zh';
    if (lang === 'english' || lang.startsWith('en')) return 'en';
    if (lang === 'korean' || lang.startsWith('ko')) return 'ko';
    if (lang === 'japanese' || lang.startsWith('ja')) return 'ja';
    if (lang === 'spanish' || lang.startsWith('es')) return 'es';
    if (lang === 'french' || lang.startsWith('fr')) return 'fr';
    if (lang === 'german' || lang.startsWith('de')) return 'de';
    
    return lang.split('-')[0];
  }, [userSettings?.languageSetting]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const stored = window.localStorage.getItem(WORD_MASK_STORAGE_KEY);
    if (stored !== null) {
      setShowWordMask(stored !== "false");
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(
      WORD_MASK_STORAGE_KEY,
      showWordMask ? "true" : "false",
    );
  }, [showWordMask]);
  
  const lines = useMemo(
    () => {
      // console.log('[TimeDrivenTranscript] Building timeline from groups:', groupedSentences?.length);
      // if (groupedSentences?.length > 0) {
      //   console.log('[TimeDrivenTranscript] First group sample:', groupedSentences[0]);
      // }
      return buildWordTimeline(groupedSentences ?? []);
    },
    [groupedSentences],
  );

  const [isTranslating, setIsTranslating] = useState(false);

  // 🌟 Trigger missing translations check
  useEffect(() => {
    if (!showTranslation || !episodeId || lines.length === 0) return;

    const checkForMissingTranslations = async () => {
        // Check if we have missing translations for the current language
        const needsTranslation = lines.some(line => {
            const raw = line.translation;
            if (!raw) {
                // console.log(`[TimeDrivenTranscript] Missing raw translation for line ${line.id}`);
                return true;
            }
            if (typeof raw === 'string') return userTargetLanguageCode !== 'zh'; // string assumed Chinese
            if (typeof raw === 'object') {
                const hasKey = !!raw[userTargetLanguageCode];
                if (!hasKey) {
                    // console.log(`[TimeDrivenTranscript] Missing key ${userTargetLanguageCode} for line ${line.id}. Keys:`, Object.keys(raw));
                }
                return !hasKey;
            }
            return true;
        });

        if (needsTranslation) {
            // console.log(`[TimeDrivenTranscript] Missing translations detected for ${userTargetLanguageCode}, triggering fetch...`);
            setIsTranslating(true);
            // ... existing fetch logic ...
            try {
                const langMap: Record<string, string> = {
                    'zh': 'Chinese',
                    'en': 'English',
                    'ja': 'Japanese',
                    'ko': 'Korean',
                };
                
                const response = await fetch(`/api/episodes/${episodeId}/translate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        targetLanguage: langMap[userTargetLanguageCode] || 'Chinese'
                    })
                });

                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.translatedTranscript) {
                        // console.log('[TimeDrivenTranscript] Translation fetch successful, updating transcript...');
                        // Notify parent to update data
                        if (onTranscriptUpdate) {
                            onTranscriptUpdate(result.translatedTranscript);
                        } else {
                            console.warn('[TimeDrivenTranscript] onTranscriptUpdate not provided, cannot update UI automatically');
                        }
                    }
                } else {
                    console.error('[TimeDrivenTranscript] Translation fetch failed:', response.status);
                }
            } catch (e) {
                console.error("[TimeDrivenTranscript] Failed to trigger translation补全", e);
            } finally {
                setIsTranslating(false);
            }
        }
    };

    const timeoutId = setTimeout(checkForMissingTranslations, 1000);
    return () => clearTimeout(timeoutId);
  }, [showTranslation, episodeId, userTargetLanguageCode, lines]);


  const handleDragEnd = useCallback((_event: any, info: { offset: { x: number }; velocity: { x: number } }) => {
    if (!enableSwipeToVocabulary) {
      return;
    }

    const threshold = 100; // 滑动距离阈值（像素）
    const velocityThreshold = 500; // 滑动速度阈值（像素/秒）
    const { offset, velocity } = info;

    // 只有当滑动距离足够大，或者速度足够快时，才触发切换
    const isSignificantSwipe = Math.abs(offset.x) > threshold || Math.abs(velocity.x) > velocityThreshold;

    if (!isSignificantSwipe) {
      return;
    }

    // 三视图滑动逻辑：transcript <-> keywords <-> vocabulary
    if (offset.x < -threshold) {
      // 向左滑动
      if (currentView === 'transcript') {
        setCurrentView('keywords');
        onViewChange?.('keywords');
      } else if (currentView === 'keywords') {
        setCurrentView('vocabulary');
        onViewChange?.('vocabulary');
      }
    } else if (offset.x > threshold) {
      // 向右滑动
      if (currentView === 'vocabulary') {
        setCurrentView('keywords');
        onViewChange?.('keywords');
      } else if (currentView === 'keywords') {
        setCurrentView('transcript');
        onViewChange?.('transcript');
      }
    }
  }, [currentView, enableSwipeToVocabulary, onViewChange]);

  const totalDuration = useMemo(() => {
    if (lines.length === 0) return 0;
    return lines.reduce((acc, line) => Math.max(acc, line.end), 0);
  }, [lines]);

  const activeLineIndex = useMemo(() => {
    if (!Number.isFinite(currentTime)) return -1;
    
    // 找出所有匹配的行（处理时间戳重叠的情况）
    const matchingIndices: number[] = [];
    lines.forEach((line, index) => {
      if (currentTime >= line.start - 0.05 && currentTime <= line.end + 0.05) {
        matchingIndices.push(index);
      }
    });
    
    if (matchingIndices.length === 0) return -1;
    if (matchingIndices.length === 1) return matchingIndices[0];
    
    // 如果有多个匹配，选择 start 最接近 currentTime 的那个
    // 这样当用户跳转到下一个 chunk 时，即使有重叠也会选择正确的行
    let bestIndex = matchingIndices[0];
    let bestDistance = Math.abs(currentTime - lines[bestIndex].start);
    
    for (let i = 1; i < matchingIndices.length; i++) {
      const idx = matchingIndices[i];
      const distance = Math.abs(currentTime - lines[idx].start);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = idx;
      }
    }
    
    return bestIndex;
  }, [lines, currentTime]);

  const highlightVocabularyMap = useMemo(
    () =>
      buildVocabularyHighlightMap(
        lines,
        activeVocabularyTerms,
        vocabularyTerms,
        staticVocabularyTerms,
      ),
    [lines, activeVocabularyTerms, vocabularyTerms, staticVocabularyTerms],
  );

  const textPalette = useMemo<TranscriptTextPalette | null>(() => {
    if (!backgroundTone) {
      return null;
    }

    const isLightBackground = backgroundTone === "light";
    if (isLightBackground) {
      return {
        current: "rgba(15, 23, 42, 0.94)",
        past: "rgba(30, 41, 59, 0.78)",
        future: "rgba(51, 65, 85, 0.54)",
        translation: "rgba(51, 65, 85, 0.68)",
        translationActive: "rgba(15, 23, 42, 0.92)",
      };
    }

    return {
      current: "rgba(255, 255, 255, 0.95)",
      past: "rgba(255, 255, 255, 0.76)",
      future: "rgba(255, 255, 255, 0.58)",
      translation: "rgba(255, 255, 255, 0.68)",
      translationActive: "rgba(255, 255, 255, 0.92)",
    };
  }, [backgroundTone]);

  const shouldShowSkeleton = isLoading && lines.length === 0;
  const shouldShowEmptyState =
    !isLoading && lines.length === 0 && Boolean(emptyStateMessage);
  const containerToneClass =
    backgroundTone === "light"
      ? "border-white/60 bg-white/80 shadow-[0_28px_100px_-54px_rgba(15,23,42,0.25)]"
      : "border-white/10 bg-slate-950/80 shadow-[0_40px_120px_-60px_rgba(15,23,42,0.85)]";

  const headerBorderClass =
    backgroundTone === "light" ? "border-slate-200/70" : "border-white/10";

  const accentBarClass =
    backgroundTone === "light" ? "bg-red-500" : "bg-red-500";

  const overlayGradientClass =
    backgroundTone === "light"
      ? "from-primary/25 via-transparent to-sky-400/10"
      : "from-white/20 via-transparent to-blue-400/15";

  if (lines.length === 0 && !shouldShowSkeleton && !shouldShowEmptyState) {
    return null;
  }

  return (
    <div className={cn("triscript-container relative isolate", className?.includes('flex-1') ? 'flex-1 flex flex-col min-h-0' : '')}>
      <div
        ref={containerRef}
        className={cn(
          "transcript-content-container relative isolate mt-1 flex flex-col overflow-hidden rounded-[28px] border backdrop-blur-2xl transition-[background,box-shadow] duration-300",
          containerToneClass,
          className?.includes('flex-1') ? 'flex-1 min-h-0' : '',
          className,
        )}
      >
        {/* View Indicators - 页面指示器在顶部中心 */}
        <div className="flex items-center justify-center pt-1.5 pb-0">
        {enableSwipeToVocabulary && (vocabularyContent || keywordsContent) && (
          
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setCurrentView('transcript');
                  onViewChange?.('transcript');
                }}
                className="h-1.5 w-1.5 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: currentView === 'transcript' 
                    ? (backgroundTone === 'light' ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.9)')
                    : (backgroundTone === 'light' ? 'rgba(15, 23, 42, 0.3)' : 'rgba(255, 255, 255, 0.3)'),
                }}
                aria-label="Transcript view"
              />
              {keywordsContent && (
                <button
                  onClick={() => {
                    setCurrentView('keywords');
                    onViewChange?.('keywords');
                  }}
                  className="h-1.5 w-1.5 rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: currentView === 'keywords'
                      ? (backgroundTone === 'light' ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.9)')
                      : (backgroundTone === 'light' ? 'rgba(15, 23, 42, 0.3)' : 'rgba(255, 255, 255, 0.3)'),
                  }}
                  aria-label="Keywords view"
                />
              )}
              {vocabularyContent && (
                <button
                  onClick={() => {
                    setCurrentView('vocabulary');
                    onViewChange?.('vocabulary');
                  }}
                  className="h-1.5 w-1.5 rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: currentView === 'vocabulary'
                      ? (backgroundTone === 'light' ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.9)')
                      : (backgroundTone === 'light' ? 'rgba(15, 23, 42, 0.3)' : 'rgba(255, 255, 255, 0.3)'),
                  }}
                  aria-label="Vocabulary view"
                />
              )}
            </div> 
        )}
        </div>
        
        <div
          className={cn(
            "relative z-[1] flex items-center justify-between gap-3 border-b px-4 pb-1 sm:px-6 sm:py-4",
            headerBorderClass,
          )}
        >
          <div className="flex items-center space-x-3">
            <div className={cn("h-[18px] w-[5px] rounded-full", accentBarClass)} />
            <h3
              className="text-md font-bold text-foreground"
              style={textPalette ? { color: textPalette.translationActive } : undefined}
            >
              {currentView === 'transcript' 
                ? t("transcript.title") 
                : currentView === 'keywords' 
                  ? t("keywords.title", "Key-words") 
                  : t("vocabulary.title", "Vocabulary")}
            </h3>
          </div>
          {currentView === 'transcript' && (
            <div className="flex items-center gap-2">
              {onToggleTranslation && (
                <button
                  onClick={() => {
                    onToggleTranslation();
                    // 切换翻译后触发自动滚动
                    if (onScrollToActive) {
                      setTimeout(() => {
                        onScrollToActive();
                      }, 100);
                    }
                  }}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                    showTranslation
                      ? "bg-primary/10 text-primary"
                      : "bg-muted/50 text-muted-foreground hover:bg-muted"
                  )}
                  aria-label={
                    showTranslation
                      ? t("transcript.hideTranslation", "Hide translation")
                      : t("transcript.showTranslation", "Show translation")
                  }
                >
                  {isTranslating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m5 8 6 6" />
                      <path d="m4 14 6-6 2-3" />
                      <path d="M2 5h12" />
                      <path d="M7 2h1" />
                      <path d="m22 22-5-10-5 10" />
                      <path d="M14 18h6" />
                    </svg>
                  )}
                  {isTranslating 
                    ? t("transcript.translating", "Translating...") 
                    : (showTranslation ? t("transcript.hide", "") : t("transcript.show", ""))}
                </button>
              )}
              {onToggleDynamicIslandVocabulary && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex h-7 w-7 items-center justify-center rounded-xl border border-border text-muted-foreground transition hover:text-foreground"
                      aria-label={t(
                        "transcript.dynamicIslandSettings",
                        "Dynamic Island settings"
                      )}
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-28 space-y-2 p-3">
                    {/* <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t("transcript.dynamicIslandTitle", "Dynamic Island")}
                      </span>
                      <p className="text-xs text-muted-foreground/80">
                        {t(
                          "transcript.dynamicIslandDescription",
                          "Control what appears in the Dynamic Island while learning."
                        )}
                      </p>
                    </div> */}
                    <div className="flex items-center justify-between text-sm font-medium text-foreground">
                      <div className="flex flex-col">
                        <span>
                          {t(
                            "transcript.dynamicIslandVocabulary",
                            "Vocab Island"
                          )}
                        </span>
                        {/* <span className="text-xs font-normal text-muted-foreground">
                          {t(
                            "transcript.dynamicIslandVocabularyHint",
                            "Show vocab terms in the Island overlay"
                          )}
                        </span> */}
                      </div>
                      <Switch
                        className="data-[state=checked]:bg-black data-[state=unchecked]:bg-muted"
                        checked={showDynamicIslandVocabulary}
                        onCheckedChange={(checked) =>
                          onToggleDynamicIslandVocabulary(checked)
                        }
                        aria-label={t(
                          "transcript.toggleDynamicIslandVocabulary",
                          "Toggle Dynamic Island vocabulary"
                        )}
                      />
                    </div>
                    {/* <div className="flex items-center justify-between text-sm font-medium text-foreground">
                      <span>
                        {t("transcript.wordMask", "Word mask")}
                      </span>
                      <Switch
                        className="data-[state=checked]:bg-black data-[state=unchecked]:bg-muted"
                        checked={showWordMask}
                        onCheckedChange={(checked) => setShowWordMask(checked)}
                        aria-label={t(
                          "transcript.toggleWordMask",
                          "Toggle transcript mask"
                        )}
                      />
                    </div> */}
                    {/* 🎯 Chapters Toggle */}
                    {onToggleChapters && (
                      <div className="flex items-center justify-between text-sm font-medium text-foreground">
                        <span>
                          {t("transcript.chapters", "Chapters")}
                        </span>
                        <Switch
                          className="data-[state=checked]:bg-black data-[state=unchecked]:bg-muted"
                          checked={showChapters}
                          onCheckedChange={(checked) => onToggleChapters(checked)}
                          aria-label={t(
                            "transcript.toggleChapters",
                            "Toggle chapters display"
                          )}
                        />
                      </div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </div>
        {enableSwipeToVocabulary && (keywordsContent || vocabularyContent) ? (
          <div
            className="relative z-[1] w-full"
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              contain: 'layout style paint',
              isolation: 'isolate',
            }}
          >
            <motion.div
              className="relative h-full w-full"
              drag={enableSwipeToVocabulary ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.1}
              dragDirectionLock
              dragMomentum={false}
              onDragEnd={handleDragEnd}
            >
              {/* Transcript View */}
              <div 
                ref={scrollContainerRef || undefined}
                className={cn(
                  "absolute inset-0 flex flex-col gap-0 px-1 py-4 sm:gap-2 sm:px-4 md:gap-2 md:px-6 lg:px-8",
                  contentWrapperClassName?.includes('flex-1') ? 'pb-2' : 'pb-60',
                  isVideoMinimized && "px-3 py-3 sm:px-3 md:px-4 lg:px-6",
                  contentWrapperClassName,
                )}
                style={{ 
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  opacity: currentView === 'transcript' ? 1 : 0,
                  pointerEvents: currentView === 'transcript' ? 'auto' : 'none',
                  visibility: currentView === 'transcript' ? 'visible' : 'hidden',
                  transitionProperty: 'opacity, visibility',
                  transitionDuration: '0.3s',
                  transitionTimingFunction: 'ease-in-out',
                  zIndex: currentView === 'transcript' ? 3 : 1,
                }}
              >
                {shouldShowSkeleton ? (
                  <div
                    className="flex flex-col gap-3 py-6 animate-pulse"
                    aria-live="polite"
                    aria-busy="true"
                  >
                    {SKELETON_BLUEPRINT.map((segments, index) => (
                      <div key={index} className="flex flex-wrap gap-3">
                        {segments.map((width, segmentIndex) => (
                          <div
                            key={segmentIndex}
                            className={cn(
                              "h-5 sm:h-6 rounded-full bg-muted-foreground/20 dark:bg-muted-foreground/40",
                              width,
                            )}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                ) : lines.length === 0 ? (
                  <div className="flex min-h-[140px] items-center justify-center px-4 py-8 text-sm text-muted-foreground sm:text-base">
                    {emptyStateMessage ?? t("transcript.empty", "No subtitles available.")}
                  </div>
                ) : (
                  lines.map((line, index) => (
                    <TranscriptLine
                      key={line.id}
                      line={line}
                      totalDuration={totalDuration}
                      currentTime={currentTime}
                      showWordMask={showWordMask}
                      onSeek={onSeek}
                      highlightWord={highlightWord}
                      highlightTextClassName={highlightTextClassName}
                      highlightBgClassName={highlightBgClassName}
                      textPalette={textPalette}
                      isActive={index === activeLineIndex}
                      showTranslation={showTranslation}
                      isVideoMinimized={isVideoMinimized}
                      highlightVocabularyMap={highlightVocabularyMap}
                      onWordClick={onWordClick}
                      autoScrollToActive={autoScrollToActive}
                      lineIndex={index}
                      scrollContainerRef={scrollContainerRef}
                      isPlayerPlaying={isPlayerPlaying}
                      liveTimeGetter={liveTimeGetter}
                      userTargetLanguageCode={userTargetLanguageCode}
                      episodeId={episodeId}
                      isTranslating={isTranslating}
                    />
                  ))
                )}
              </div>
              
              {/* Keywords View */}
              {keywordsContent && (
                <div 
                  className={cn(
                    "absolute inset-0 flex flex-col gap-0 px-1 py-0.5 sm:gap-2 sm:px-4 md:gap-2 md:px-6 lg:px-8",
                    contentWrapperClassName?.includes('flex-1') ? 'pb-2' : 'pb-60',
                    isVideoMinimized && "px-3 py-3 sm:px-3 md:px-4 lg:px-6",
                    contentWrapperClassName,
                  )}
                  style={{ 
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    opacity: currentView === 'keywords' ? 1 : 0,
                    pointerEvents: currentView === 'keywords' ? 'auto' : 'none',
                    visibility: currentView === 'keywords' ? 'visible' : 'hidden',
                    transitionProperty: 'opacity, visibility',
                    transitionDuration: '0.3s',
                    transitionTimingFunction: 'ease-in-out',
                    zIndex: currentView === 'keywords' ? 3 : 2,
                  }}
                >
                  {keywordsContent}
                </div>
              )}
              
              {/* Vocabulary View */}
              {vocabularyContent && (
                <div 
                  className={cn(
                    "absolute inset-0 flex flex-col gap-0 px-1 py-0.5 sm:gap-2 sm:px-4 md:gap-2 md:px-6 lg:px-8",
                    contentWrapperClassName?.includes('flex-1') ? 'pb-2' : 'pb-60',
                    isVideoMinimized && "px-3 py-3 sm:px-3 md:px-4 lg:px-6",
                    contentWrapperClassName,
                  )}
                  style={{ 
                    overflowY: 'auto',
                    overflowX: 'visible',
                    opacity: currentView === 'vocabulary' ? 1 : 0,
                    pointerEvents: currentView === 'vocabulary' ? 'auto' : 'none',
                    visibility: currentView === 'vocabulary' ? 'visible' : 'hidden',
                    transitionProperty: 'opacity, visibility',
                    transitionDuration: '0.3s',
                    transitionTimingFunction: 'ease-in-out',
                    zIndex: currentView === 'vocabulary' ? 3 : 2,
                    "--tdt-vocab-bleed": "clamp(16px, 6vw, 32px)",
                  } as React.CSSProperties}
                >
                  {vocabularyContent}
                </div>
              )}
            </motion.div>
          </div>
        ) : (
          <div
            ref={scrollContainerRef || undefined}
            className={cn(
              "relative z-[1] mx-auto flex w-full max-w-4xl flex-col gap-0 px-1 py-4 sm:gap-2 sm:px-4 md:gap-2 md:px-6 lg:px-8",
              contentWrapperClassName?.includes('flex-1') ? 'pb-2' : 'pb-60',
              isVideoMinimized && "px-3 py-3 sm:px-3 md:px-4 lg:px-6",
              contentWrapperClassName,
            )}
          >
            {shouldShowSkeleton ? (
              <div
                className="flex flex-col gap-3 py-6 animate-pulse"
                aria-live="polite"
                aria-busy="true"
              >
                {SKELETON_BLUEPRINT.map((segments, index) => (
                  <div key={index} className="flex flex-wrap gap-3">
                    {segments.map((width, segmentIndex) => (
                      <div
                        key={segmentIndex}
                        className={cn(
                          "h-5 sm:h-6 rounded-full bg-muted-foreground/20 dark:bg-muted-foreground/40",
                          width,
                        )}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ) : lines.length === 0 ? (
              <div className="flex min-h-[140px] items-center justify-center px-4 py-8 text-sm text-muted-foreground sm:text-base">
                {emptyStateMessage ?? t("transcript.empty", "No subtitles available.")}
              </div>
            ) : (
              lines.map((line, index) => (
                <TranscriptLine
                  key={line.id}
                  line={line}
                  totalDuration={totalDuration}
                  currentTime={currentTime}
                  showWordMask={showWordMask}
                  onSeek={onSeek}
                  highlightWord={highlightWord}
                  highlightTextClassName={highlightTextClassName}
                  highlightBgClassName={highlightBgClassName}
                  textPalette={textPalette}
                  isActive={index === activeLineIndex}
                  showTranslation={showTranslation}
                  isVideoMinimized={isVideoMinimized}
                  highlightVocabularyMap={highlightVocabularyMap}
                  onWordClick={onWordClick}
                  autoScrollToActive={autoScrollToActive}
                  lineIndex={index}
                  scrollContainerRef={scrollContainerRef}
                  isPlayerPlaying={isPlayerPlaying}
                  liveTimeGetter={liveTimeGetter}
                  userTargetLanguageCode={userTargetLanguageCode}
                  episodeId={episodeId}
                  isTranslating={isTranslating}
                />
              ))
            )}
          </div>
        )}
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 z-0 bg-gradient-to-br opacity-80",
            overlayGradientClass,
          )}
        />
      </div>
    </div>
  );
};

export default TimeDrivenTranscript;
